import React, { useRef, useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import LicenseData from "./LicenseData";

function LicenseSection() {
  const sliderRef = useRef(null);
  const autoScrollRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const isUserInteracting = useRef(false);

  const [selectedImage, setSelectedImage] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | DUPLICATE DATA UNTUK INFINITE LOOP
  |--------------------------------------------------------------------------
  */

  const infiniteData = [...LicenseData, ...LicenseData];

  /*
  |--------------------------------------------------------------------------
  | STOP AUTO SCROLL
  |--------------------------------------------------------------------------
  */

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | START AUTO SCROLL
  |--------------------------------------------------------------------------
  */

  const startAutoScroll = () => {
    const slider = sliderRef.current;

    if (!slider) return;

    stopAutoScroll();

    autoScrollRef.current = setInterval(() => {
      const currentSlider = sliderRef.current;

      if (!currentSlider) return;

      if (isUserInteracting.current) return;

      currentSlider.scrollLeft += 1;

      const oneSetWidth = currentSlider.scrollWidth / 2;

      /*
      |--------------------------------------------------------------------------
      | KEMBALI KE AWAL
      |--------------------------------------------------------------------------
      */

      if (currentSlider.scrollLeft >= oneSetWidth) {
        currentSlider.scrollLeft = 0;
      }
    }, 35);
  };

  /*
  |--------------------------------------------------------------------------
  | RESUME AUTO SCROLL
  |--------------------------------------------------------------------------
  */

  const resumeAutoScroll = () => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }

    resumeTimerRef.current = setTimeout(() => {
      isUserInteracting.current = false;
      startAutoScroll();
    }, 1800);
  };

  /*
  |--------------------------------------------------------------------------
  | USER MULAI MENGGESER
  |--------------------------------------------------------------------------
  */

  const userStartInteraction = () => {
    isUserInteracting.current = true;

    stopAutoScroll();

    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | USER SELESAI MENGGESER
  |--------------------------------------------------------------------------
  */

  const userEndInteraction = () => {
    resumeAutoScroll();
  };

  /*
  |--------------------------------------------------------------------------
  | TOUCH HP
  |--------------------------------------------------------------------------
  */

  const handleTouchStart = () => {
    userStartInteraction();
  };

  const handleTouchEnd = () => {
    userEndInteraction();
  };

  const handleTouchCancel = () => {
    userEndInteraction();
  };

  /*
  |--------------------------------------------------------------------------
  | TOUCHPAD / MOUSE WHEEL
  |--------------------------------------------------------------------------
  */

  const handleWheel = () => {
    userStartInteraction();
    userEndInteraction();
  };

  /*
  |--------------------------------------------------------------------------
  | BUTTON KIRI
  |--------------------------------------------------------------------------
  */

  const scrollLeft = () => {
    const slider = sliderRef.current;

    if (!slider) return;

    userStartInteraction();

    slider.scrollBy({
      left: -1100,
      behavior: "smooth",
    });

    resumeAutoScroll();
  };

  /*
  |--------------------------------------------------------------------------
  | BUTTON KANAN
  |--------------------------------------------------------------------------
  */

  const scrollRight = () => {
    const slider = sliderRef.current;

    if (!slider) return;

    userStartInteraction();

    slider.scrollBy({
      left: 1100,
      behavior: "smooth",
    });

    resumeAutoScroll();
  };

  /*
  |--------------------------------------------------------------------------
  | START OTOMATIS SAAT HALAMAN DIBUKA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    startAutoScroll();

    return () => {
      stopAutoScroll();

      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
      }
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | MODAL
  |--------------------------------------------------------------------------
  */

  const openModal = (item) => {
    userStartInteraction();

    setSelectedImage(item);
  };

  const closeModal = () => {
    setSelectedImage(null);

    resumeAutoScroll();
  };

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <Container fluid className="license-section">
      <Container>
        <h1 className="project-heading">
          Licenses & <strong className="purple">Certifications</strong>
        </h1>

        <p className="license-subtitle">
          A collection of certifications, awards, and achievements earned
          throughout my academic and professional journey.
        </p>

        <div className="license-slider-wrapper">
          {/* BUTTON KIRI */}

          <button
            type="button"
            className="license-nav-btn left"
            onClick={scrollLeft}
            aria-label="Previous certificates"
          >
            <FaChevronLeft />
          </button>

          {/* SLIDER */}

          <div
            className="license-slider"
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            onWheel={handleWheel}
          >
            {infiniteData.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="license-card"
                onClick={() => openModal(item)}
              >
                <div className="license-image">
                  <img src={item.image} alt={item.title} draggable="false" />
                </div>

                <div className="license-content">
                  <h5>{item.title}</h5>

                  <span>{item.issuer}</span>
                </div>
              </div>
            ))}
          </div>

          {/* BUTTON KANAN */}

          <button
            type="button"
            className="license-nav-btn right"
            onClick={scrollRight}
            aria-label="Next certificates"
          >
            <FaChevronRight />
          </button>
        </div>
      </Container>

      {/* MODAL */}

      {selectedImage && (
        <div className="license-modal" onClick={closeModal}>
          <div
            className="license-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="close-modal" onClick={closeModal}>
              <FaTimes />
            </button>

            <img src={selectedImage.image} alt={selectedImage.title} />

            <h4>{selectedImage.title}</h4>

            <p>{selectedImage.issuer}</p>
          </div>
        </div>
      )}
    </Container>
  );
}

export default LicenseSection;
